import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Star, Pencil } from "lucide-react";

const EMPTY = { name: "", code: "", tracking_url_template: "", active: true, is_default: false };

export default function CarriersTab({ isAdmin }) {
  const { t } = useI18n();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => api.get("/carriers").then((r) => setRows(r.data))
    .catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const start = (row) => {
    setEditing(row?.id || null);
    setF(row ? { name: row.name, code: row.code,
      tracking_url_template: row.tracking_url_template || "",
      active: row.active !== false, is_default: !!row.is_default } : EMPTY);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/carriers/${editing}`, f);
      else await api.post("/carriers", f);
      setOpen(false);
      load();
      toast.success(t("common.saved"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const setDefault = async (row) => {
    try {
      await api.put(`/carriers/${row.id}`, {
        name: row.name, code: row.code,
        tracking_url_template: row.tracking_url_template || "",
        active: true, is_default: true });
      load();
      toast.success(t("common.saved"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (row) => {
    setConfirmDel(null);
    try { await api.delete(`/carriers/${row.id}`); load(); toast.success(t("common.deleted")); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="carriers-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h3 className="font-heading text-lg font-medium">{t("carr.title")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("carr.subtitle")}</p>
        </div>
        {isAdmin && (
          <Button onClick={() => start(null)} data-testid="carrier-add">
            <Plus className="mr-2 h-4 w-4" />{t("carr.add")}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr>
            <Th>{t("common.name")}</Th><Th>{t("carr.code")}</Th>
            <Th>{t("carr.trackingUrl")}</Th><Th>{t("carr.active")}</Th>
            <Th>{t("carr.default")}</Th>{isAdmin && <Th />}
          </tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`carrier-row-${r.code}`}>
                <Td>{r.name}</Td>
                <Td mono>{r.code}</Td>
                <Td className="max-w-[22rem] truncate text-xs text-muted-foreground">
                  {r.tracking_url_template || "—"}
                </Td>
                <Td>{r.active !== false ? t("common.yes") : t("common.no")}</Td>
                <Td>
                  {r.is_default
                    ? <span className="inline-flex items-center gap-1 text-primary"><Star className="h-3.5 w-3.5" />{t("carr.default")}</span>
                    : isAdmin && r.active !== false
                      ? <button onClick={() => setDefault(r)} className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground" data-testid={`carrier-default-${r.code}`}>{t("carr.setDefault")}</button>
                      : "—"}
                </Td>
                {isAdmin && (
                  <Td right>
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => start(r)} data-testid={`carrier-edit-${r.code}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setConfirmDel(r)} data-testid={`carrier-delete-${r.code}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{t("carr.inactiveHint")}</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? t("carr.title") : t("carr.add")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("common.name")}</Label>
              <Input className="mt-1.5" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="carrier-name" /></div>
            <div><Label>{t("carr.code")}</Label>
              <Input className="mt-1.5" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="carrier-code" /></div>
            <div><Label>{t("carr.trackingUrl")}</Label>
              <Input className="mt-1.5 font-mono text-xs" placeholder="https://…{tracking}" value={f.tracking_url_template} onChange={(e) => setF({ ...f, tracking_url_template: e.target.value })} data-testid="carrier-tracking-url" />
              <p className="mt-1 text-xs text-muted-foreground">{t("carr.trackingHint")}</p></div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <Label htmlFor="carrier-active">{t("carr.active")}</Label>
              <Switch id="carrier-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="carrier-active" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <Label htmlFor="carrier-is-default">{t("carr.default")}</Label>
              <Switch id="carrier-is-default" checked={f.is_default} onCheckedChange={(v) => setF({ ...f, is_default: v, active: v ? true : f.active })} data-testid="carrier-is-default" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={save} data-testid="carrier-save">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("carr.confirmDelete")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {confirmDel?.name} — {t("carr.confirmDeleteHint")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={() => remove(confirmDel)} data-testid="carrier-delete-confirm">
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
